import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-950">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-800 mx-auto mb-4">
              <i className="ri-file-close-line text-3xl text-foreground-600" />
            </div>
            <h1 className="text-xl font-bold text-foreground-300 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line" /> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: article.categoryName, href: `/category/${article.categorySlug}` },
    { label: article.title.slice(0, 20) + '...' },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    url: `${siteInfo.url}/article/${article.id}`,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Person',
      name: article.author,
      url: `${siteInfo.url}/author/${encodeURIComponent(article.author)}`,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      logo: 'http://jtqzai.cn/logo.png',
    },
    articleSection: article.categoryName,
    keywords: article.tags.join(','),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: article.title,
    description: article.summary,
    url: `${siteInfo.url}/article/${article.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntity: articleSchema,
  };

  return (
    <>
      <title>{`${article.title} - ${siteInfo.name}`}</title>
      <meta name="description" content={article.summary} />
      <meta name="keywords" content={`${article.tags.join(',')},51黑料网,51黑料吃瓜网`} />
      <link rel="canonical" href={`http://jtqzai.cn/article/${article.id}`} />
      <meta property="og:type" content="article" />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.summary} />
      <meta property="og:url" content={`http://jtqzai.cn/article/${article.id}`} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:section" content={article.categoryName} />
      <meta property="article:tag" content={article.tags.join(',')} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.summary} />
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen flex flex-col bg-background-950">
        <header>
          <Navbar />
        </header>

        <main className="pt-20 md:pt-24">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-8 py-6 md:py-10">
            <div className="max-w-4xl mx-auto">
              {/* Article Header */}
              <header className="mb-6 md:mb-8">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-2.5 py-1 rounded-md bg-primary-500/15 text-primary-300 text-xs font-semibold hover:bg-primary-500/25 transition-colors"
                  >
                    {article.categoryName}
                  </Link>
                  {article.isExclusive && (
                    <span className="px-2.5 py-1 rounded-md bg-accent-500/15 text-accent-300 text-xs font-bold">
                      <i className="ri-vip-crown-line mr-1" />独家
                    </span>
                  )}
                  {article.isHot && (
                    <span className="px-2.5 py-1 rounded-md bg-secondary-500/15 text-secondary-300 text-xs font-bold">
                      <i className="ri-fire-line mr-1" />热门
                    </span>
                  )}
                </div>

                <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-foreground-100 leading-snug mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <div className="w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 text-xs font-bold">
                      {article.author[0]}
                    </div>
                    <span className="font-medium text-foreground-300">{article.author}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {(article.views / 10000).toFixed(1)}万阅读
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="rounded-xl overflow-hidden mb-6 md:mb-8">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-56 md:h-80 lg:h-96 object-cover object-top"
                  title={`${article.title} - 51黑料网`}
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-invert prose-sm md:prose-base max-w-none text-foreground-300 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="mt-8 pt-6 border-t border-background-800/50">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${article.categorySlug}`}
                      className="px-3 py-1 rounded-full bg-background-800 border border-background-700/50 text-xs text-foreground-400 hover:text-primary-300 hover:border-primary-500/30 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900/50 border-t border-background-800/50">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6 md:mb-8">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-500/20 text-secondary-400">
                    <i className="ri-links-line" />
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-foreground-100">相关推荐</h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group flex gap-3 p-3 rounded-xl bg-background-900 border border-background-800/50 hover:border-primary-500/30 transition-all"
                    >
                      <div className="w-24 h-18 md:w-28 md:h-20 rounded-lg overflow-hidden shrink-0">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-foreground-300 group-hover:text-primary-300 transition-colors line-clamp-2">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-xs text-foreground-600">
                          <span>{ra.publishDate}</span>
                          <span>{(ra.views / 10000).toFixed(1)}万</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}